import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Sparkles, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const navigate = useNavigate();
    const [state, setState] = useState({ phase: "checking", attempts: 0, data: null });

    useEffect(() => {
        if (!sessionId) {
            setState({ phase: "error", message: "Missing session_id", attempts: 0 });
            return;
        }
        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 8;

        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState({ phase: "success", data, attempts });
                    return;
                }
                if (data.status === "expired") {
                    setState({ phase: "expired", data, attempts });
                    return;
                }
                attempts += 1;
                if (attempts >= maxAttempts) {
                    setState({ phase: "timeout", data, attempts });
                    return;
                }
                setState({ phase: "pending", data, attempts });
                setTimeout(poll, 2000);
            } catch (e) {
                setState({ phase: "error", message: e.response?.data?.detail || e.message, attempts });
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    const renderContent = () => {
        if (state.phase === "checking" || state.phase === "pending") {
            return (
                <>
                    <Loader2 className="h-12 w-12 mx-auto text-coral animate-spin mb-4" />
                    <h1 className="font-display text-3xl font-bold mb-2">Confirming your payment…</h1>
                    <p className="text-muted-foreground font-app">Polling Stripe for confirmation. This usually takes a few seconds. (Attempt {state.attempts + 1}/8)</p>
                </>
            );
        }
        if (state.phase === "success") {
            return (
                <>
                    <div className="h-16 w-16 rounded-full bg-fiji-green/15 grid place-items-center mx-auto mb-4">
                        <CheckCircle2 className="h-10 w-10 text-fiji-green" />
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold mb-3">Premium Activated 🌟</h1>
                    <p className="text-muted-foreground font-app mb-2">Your Joint Venture is now featured at the top of the Deal Feed.</p>
                    <p className="text-sm text-muted-foreground font-app mb-6">A premium investor blast was triggered (mocked) to our Investor Watchlist.</p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Button asChild className="bg-ocean-green text-white"><Link to="/jv">View Deal Feed <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
                        <Button asChild variant="outline"><Link to="/dashboard">Back to dashboard</Link></Button>
                    </div>
                </>
            );
        }
        if (state.phase === "expired" || state.phase === "timeout") {
            return (
                <>
                    <XCircle className="h-12 w-12 mx-auto text-coral mb-4" />
                    <h1 className="font-display text-3xl font-bold mb-3">Payment not confirmed</h1>
                    <p className="text-muted-foreground font-app mb-6">{state.phase === "expired" ? "The checkout session expired." : "We couldn't confirm payment status in time. If you completed checkout, refresh in a moment."}</p>
                    <Button onClick={() => navigate("/dashboard")} className="bg-ocean-green text-white">Back to dashboard</Button>
                </>
            );
        }
        return (
            <>
                <XCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
                <h1 className="font-display text-3xl font-bold mb-3">Something went wrong</h1>
                <p className="text-muted-foreground font-app mb-6">{state.message || "Unknown error."}</p>
                <Button onClick={() => navigate("/dashboard")} className="bg-ocean-green text-white">Back to dashboard</Button>
            </>
        );
    };

    return (
        <div data-testid="payment-success-page" className="max-w-xl mx-auto px-4 py-20">
            <Card className="p-10 text-center">
                <Sparkles className="h-5 w-5 mx-auto text-coral mb-2" />
                {renderContent()}
            </Card>
        </div>
    );
}
